import { useStartMentorPersonalDataExport, useUpdateMentorComment } from '@actions';
import Button from '@components/V4/Button';
import { useAuthState } from '@contexts/AuthContext';
import { UserRole } from '@shared/enums';
import useToast from 'apps/agora/src/hooks/useToast';
import { downloadMentorCv } from 'apps/agora/src/utils/downloadMentorCv';
import { getUserLocation } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { MentorDetailsPageContext } from '../MentorDetailsPage';
import MentorDetailsForm from './MentorDetailsForm';

interface MentorDetailsSectionProps {
  showBio?: boolean;
}

const MentorDetailsSection = (props: MentorDetailsSectionProps) => {
  const { showBio = true } = props;
  const [isEditMode, setIsEditMode] = useState(false);

  const { mentor } = useContext(MentorDetailsPageContext);
  const toast = useToast();
  const mentorCommentText = mentor?.mentorData?.adminComment?.text ?? '';
  const [comment, setComment] = useState(mentorCommentText);

  const mentorId = mentor?._id ?? '';
  const { roles } = useAuthState();
  const isSuperUser = roles.includes(UserRole.SuperUser);

  const { mutate: saveComment, isLoading: isSavingComment } = useUpdateMentorComment(mentorId, {
    onSuccess: () => {
      toast.success('Comment saved.');
    },
    onError: () => {
      toast.error('Unable to save the comment.');
    },
  });

  const { mutate: startPersonalDataExport, isLoading: isStartingPersonalDataExport } =
    useStartMentorPersonalDataExport(mentorId, {
      onSuccess: () => {
        toast.success(
          'Personal data export started. You will receive an email with the download link.'
        );
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.error?.message ?? 'The personal data export could not be started.'
        );
      },
    });

  useEffect(() => {
    if (!isEditMode) {
      setComment(mentorCommentText);
    }
  }, [mentorCommentText, isEditMode]);

  const graduationYear = useMemo(() => {
    const sortedEducationalBackground = mentor?.educationalBackground
      ?.filter((educationalBackground) => !!educationalBackground.university)
      .sort((a, b) => (b?.endYear ?? 0) - (a?.endYear ?? 0));

    return sortedEducationalBackground?.[0]?.endYear || 'n/a';
  }, [mentor?.educationalBackground]);

  const mostRelevantLastActivity = useMemo(() => {
    if (mentor?.lastActivityTimestamp && mentor?.lastLogin) {
      const lastActivity = moment(mentor?.lastActivityTimestamp).isAfter(moment(mentor?.lastLogin));
      return lastActivity ? mentor?.lastActivityTimestamp : mentor?.lastLogin;
    }

    return mentor?.lastLogin || mentor?.lastActivityTimestamp;
  }, [mentor?.lastActivityTimestamp, mentor?.lastLogin]);

  const hasCommentChanges = comment !== mentorCommentText;
  const adminCommentUpdatedAt = mentor?.mentorData?.adminComment?.updatedAt;
  const adminCommentAuthor = mentor?.mentorData?.adminComment?.updatedBy?.fullName;
  const adminCommentInfo = adminCommentUpdatedAt
    ? `${moment(adminCommentUpdatedAt).format('DD.MM.YYYY HH:mm')}${
        adminCommentAuthor ? ` by ${adminCommentAuthor}` : ''
      }`
    : 'No admin comments yet.';

  const [isDownloadingCv, setIsDownloadingCv] = useState(false);
  const handleDownloadCv = async () => {
    try {
      setIsDownloadingCv(true);
      await downloadMentorCv(mentorId);
    } catch {
      toast.error('CV could not be downloaded.');
    } finally {
      setIsDownloadingCv(false);
    }
  };

  const infoItems: { label: string; value?: ReactNode }[] = [
    { label: 'Mail:', value: mentor?.email },
    {
      label: 'Phone:',
      value: `${mentor?.phoneNumber?.countryCode ?? ''}${mentor?.phoneNumber?.number ?? ''}`,
    },
    { label: 'Graduation Year:', value: graduationYear },
    { label: 'Location:', value: getUserLocation(mentor) },
    { label: 'Timezone:', value: mentor?.timezone },
    { label: 'Language:', value: mentor?.spokenLanguages?.join(', ') },
    {
      label: 'LinkedIn:',
      value: mentor?.mentorData?.linkedin ? (
        <a
          href={mentor.mentorData.linkedin}
          target="_blank"
          rel="noreferrer"
          className="text-customPrimary break-all hover:underline"
        >
          {mentor.mentorData.linkedin}
        </a>
      ) : undefined,
    },
    {
      label: 'CV:',
      value: mentor?.mentorData?.cv ? (
        <Button
          buttonText="Download CV"
          variant="secondary"
          isLoading={isDownloadingCv}
          onClick={handleDownloadCv}
        />
      ) : undefined,
    },
    {
      label: 'Last Activity Date:',
      value: mostRelevantLastActivity
        ? moment(mostRelevantLastActivity).format('DD.MM.YYYY; HH:mm')
        : undefined,
    },
  ];

  const handleCommentSave = () => {
    if (!mentorId || !hasCommentChanges) return;
    saveComment({ comment });
  };

  const editModeChangeHandler = () => {
    setIsEditMode((editMode) => !editMode);
  };

  return isEditMode ? (
    <MentorDetailsForm
      onCloseEditMode={editModeChangeHandler}
      comment={comment}
      setComment={setComment}
      hasCommentChanges={hasCommentChanges}
      handleCommentSave={handleCommentSave}
      isSavingComment={isSavingComment}
      adminCommentInfo={adminCommentInfo}
    />
  ) : (
    <>
      <div className="flex justify-between mb-2">
        <p className="text-xs font-bold">Contact Info</p>
        <div className="flex gap-2">
          {isSuperUser && (
            <Button
              buttonText="Export personal data"
              variant="secondary"
              isLoading={isStartingPersonalDataExport}
              onClick={() => startPersonalDataExport({})}
            />
          )}
          <Button buttonText="Edit" onClick={editModeChangeHandler} />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-1 gap-x-8 gap-y-2 tablet:grid-cols-2">
          {infoItems.map((item) => (
            <span key={item.label} className="flex text-customGrey font-raleway text-xs">
              <span className="min-w-max">{item.label}</span>
              <span className="text-white ml-2 break-words">{item.value ?? 'n/a'}</span>
            </span>
          ))}
        </div>

        <span className="flex text-customGrey font-raleway text-xs">
          <span className="min-w-max">Admin comment:</span>
          <div className="ml-2 flex flex-col gap-1">
            <span className="text-white break-words">
              {mentor?.mentorData?.adminComment?.text ?? 'No admin comments yet.'}
            </span>
            <span className="text-customGrey text-xxs">{adminCommentInfo}</span>
          </div>
        </span>

        {showBio && (
          <span className="flex text-customGrey font-raleway text-xs">
            <span className="min-w-max">Bio:</span>
            <span className="text-white ml-2 line-clamp-3 break-words">
              {mentor?.mentorData?.biography || 'n/a'}
            </span>
          </span>
        )}
      </div>
    </>
  );
};

export default MentorDetailsSection;
